/**
 * The reusable typed-import wizard — "import a CSV/TSV/JSON as X".
 *
 * Presentational + framework-agnostic: it takes an `ImportTargetSpec` (which
 * declares the fields a column can map onto) and two async callbacks —
 * `onPreview` (parse a file into columns + sample) and `onRun` (land the mapped
 * rows). It owns the Upload → Map → Preview → Done flow and the auto-guess step;
 * it knows NOTHING about which capability it's importing into. Generalized from
 * the former CRM-only `csv-import-wizard.tsx` so leads, candidates, transactions
 * (and any future kind) share one surface.
 */

import { useState } from "react";
import { Button } from "../../button";
import { Card, CardContent } from "../../card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../table";
import { FileInput } from "../file-input";
import { cn } from "../../utils";
import {
  guessMapping,
  type ImportTargetSpec,
} from "./targets";

const IGNORE = "__ignore__";

export interface TypedImportWizardProps {
  target: ImportTargetSpec;
  onPreview: (file: {
    filename: string;
    file_base64: string;
  }) => Promise<{
    columns: string[];
    sample_rows: Record<string, string>[];
    total_rows: number;
  }>;
  onRun: (input: {
    filename: string;
    file_base64: string;
    mapping: Record<string, string>;
  }) => Promise<{ imported_rows: number; failed_rows: number }>;
}

export function TypedImportWizard({ target, onPreview, onRun }: TypedImportWizardProps) {
  const [step, setStep] = useState<"upload" | "map" | "preview" | "done">("upload");
  const [filename, setFilename] = useState("");
  const [fileBase64, setFileBase64] = useState("");
  const [columns, setColumns] = useState<string[]>([]);
  const [sampleRows, setSampleRows] = useState<Record<string, string>[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  // mapping: column → field value ("" = ignored)
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [result, setResult] = useState<{ imported_rows: number; failed_rows: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const fieldOptions = [
    { value: IGNORE, label: "— ignore —" },
    ...target.fields.map((f) => ({ value: f.value, label: f.label })),
  ];

  async function onFile(file: File | null) {
    setSelectedFile(file);
    if (!file) return;
    setError(null);
    const buf = await file.arrayBuffer();
    const b64 = bufferToBase64(buf);
    setFileBase64(b64);
    setFilename(file.name);
    setBusy(true);
    try {
      const p = await onPreview({ filename: file.name, file_base64: b64 });
      setColumns(p.columns);
      setSampleRows(p.sample_rows);
      setTotalRows(p.total_rows);
      setMapping(guessMapping(p.columns, target));
      setStep("map");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function run() {
    setBusy(true);
    setError(null);
    try {
      const cleaned: Record<string, string> = {};
      for (const [col, field] of Object.entries(mapping)) {
        if (field && field !== IGNORE) cleaned[col] = field;
      }
      const r = await onRun({ filename, file_base64: fileBase64, mapping: cleaned });
      setResult(r);
      setStep("done");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  function reset() {
    setStep("upload");
    setFilename("");
    setFileBase64("");
    setColumns([]);
    setSampleRows([]);
    setTotalRows(0);
    setMapping({});
    setResult(null);
    setSelectedFile(null);
  }

  return (
    <Card>
      <CardContent className="flex flex-col gap-4">
        <Stepper step={step} />
        {error && <div className="type-body text-destructive">{error}</div>}

        {step === "upload" && (
          <FileInput
            value={selectedFile}
            onChange={onFile}
            accept=".csv,.tsv,.json,text/csv,text/tab-separated-values,application/json"
            maxBytes={target.maxBytes}
            onError={setError}
            disabled={busy}
            hint={`CSV, TSV, or JSON up to ${(target.maxBytes / 1_000_000).toFixed(0)}MB. First row must be a header.`}
          />
        )}

        {step === "map" && (
          <div className="flex flex-col gap-2">
            <div className="type-body text-muted-foreground">
              {totalRows.toLocaleString()} rows in <code>{filename}</code>. Map each column to a {target.noun.replace(/s$/, "")} field.
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Column</TableHead>
                  <TableHead>Sample</TableHead>
                  <TableHead>Maps to</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {columns.map((c) => (
                  <TableRow key={c}>
                    <TableCell className="text-foreground">{c}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {sampleRows[0]?.[c] ?? ""}
                    </TableCell>
                    <TableCell>
                      <Select
                        value={mapping[c] || IGNORE}
                        onValueChange={(v) =>
                          setMapping((m) => ({ ...m, [c]: v === IGNORE ? "" : v }))
                        }
                      >
                        <SelectTrigger className="w-56">
                          <SelectValue placeholder="— ignore —" />
                        </SelectTrigger>
                        <SelectContent>
                          {fieldOptions.map((o) => (
                            <SelectItem key={o.value} value={o.value}>
                              {o.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setStep("upload")}>
                Back
              </Button>
              <Button size="sm" onClick={() => setStep("preview")}>
                Preview
              </Button>
            </div>
          </div>
        )}

        {step === "preview" && (
          <div className="flex flex-col gap-2">
            <div className="type-body text-muted-foreground">
              Sample of first {sampleRows.length} rows after mapping.
            </div>
            <pre className="max-h-60 overflow-auto rounded-md bg-muted/40 p-2 type-body">
              {sampleRows
                .map((row, i) => {
                  const mapped: Record<string, string> = {};
                  for (const [col, field] of Object.entries(mapping)) {
                    if (field && field !== IGNORE) mapped[field] = row[col] ?? "";
                  }
                  return `Row ${i + 1}: ${JSON.stringify(mapped, null, 2)}`;
                })
                .join("\n\n")}
            </pre>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setStep("map")} disabled={busy}>
                Back
              </Button>
              <Button size="sm" onClick={run} disabled={busy}>
                {busy ? "Running…" : `Import ${totalRows.toLocaleString()} rows`}
              </Button>
            </div>
          </div>
        )}

        {step === "done" && result && (
          <div className="flex flex-col gap-2 type-body">
            <div className="text-foreground">
              Imported {result.imported_rows.toLocaleString()} {target.noun}
              {result.failed_rows > 0 ? `, ${result.failed_rows.toLocaleString()} failed` : ""}.
            </div>
            <div>
              <Button size="sm" variant="outline" onClick={reset}>
                Import another
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Stepper({ step }: { step: "upload" | "map" | "preview" | "done" }) {
  const labels = ["Upload", "Map", "Preview", "Done"];
  const idx = ["upload", "map", "preview", "done"].indexOf(step);
  return (
    <ol className="flex gap-2 type-body text-muted-foreground">
      {labels.map((l, i) => (
        <li
          key={l}
          className={cn(
            "rounded-md border border-border/40 px-2 py-1",
            i === idx && "border-ring text-foreground",
            i < idx && "text-foreground",
          )}
        >
          {i + 1}. {l}
        </li>
      ))}
    </ol>
  );
}

function bufferToBase64(buf: ArrayBuffer): string {
  let bin = "";
  const bytes = new Uint8Array(buf);
  for (let i = 0; i < bytes.byteLength; i++) bin += String.fromCharCode(bytes[i]!);
  return btoa(bin);
}
